import React, { useRef, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import type { MediaSegment, TextOverlay } from '../types';
import { MIN_SEGMENT_DURATION_SEC, UGC_LIBRARY_MEDIA_DRAG_TYPE } from '../constants';
import { UGCMusicWaveform } from './UGCMusicWaveform';
import { UGCAudioWaveform } from './UGCAudioWaveform';

const RESIZE_HANDLE_WIDTH_PX = 6;
const THUMB_BOX_WIDTH_PX = 44;
/** Timeline zoom: px per second of timeline. User-adjustable via the −/+ controls and Ctrl/Cmd+scroll.
 *  Max is deep enough (20×) that one second spans ~half a screen — frame-level trims, CapCut-style. */
const DEFAULT_PIXELS_PER_SECOND = 24;
const MIN_PIXELS_PER_SECOND = 12;
const MAX_PIXELS_PER_SECOND = 480;
const TRACK_HEIGHT = 40;
const TEXT_LANE_HEIGHT = 26;
const VIDEO_LANE_HEIGHT = TRACK_HEIGHT; // one lane == the single-row height
const MUSIC_TRACK_HEIGHT = 30;
const LABEL_COLUMN_WIDTH = 72;

/** Round to a step without float dust (0.30000000000000004 → 0.3). */
const roundToStep = (v: number, step: number) => parseFloat((Math.round(v / step) * step).toFixed(2));

/**
 * Greedy interval scheduling: assigns each interval to the first lane where it doesn't
 * overlap one already placed there. Returns a lane index per item, parallel to the input
 * order (so callers can map results back by id or by array index). Lanes are a display-only
 * affordance — they stack colliding clips/overlays into rows and never touch time/duration.
 */
function assignLanes(intervals: { time: number; duration: number }[]): number[] {
  const order = intervals
    .map((interval, index) => ({ index, time: interval.time, duration: interval.duration }))
    .sort((a, b) => a.time - b.time);
  const laneEndTime: number[] = [];
  const lanes = new Array<number>(intervals.length).fill(0);
  for (const interval of order) {
    let lane = laneEndTime.findIndex(end => end <= interval.time);
    if (lane === -1) lane = laneEndTime.length;
    laneEndTime[lane] = interval.time + interval.duration;
    lanes[interval.index] = lane;
  }
  return lanes;
}

/** Lane index per text overlay, keyed by overlay id. */
function assignTextOverlayLanes(overlays: TextOverlay[]): Map<string, number> {
  const lanes = assignLanes(overlays);
  return new Map(overlays.map((overlay, index) => [overlay.id, lanes[index]]));
}

function maxDurationForSegment(
  segments: MediaSegment[],
  mediaId: string,
  segmentTime: number,
  total: number
): number {
  let max = total - segmentTime;
  for (const other of segments) {
    if (other.media_id === mediaId) continue;
    if (other.time > segmentTime) max = Math.min(max, other.time - segmentTime);
  }
  return Math.max(MIN_SEGMENT_DURATION_SEC, max);
}

export interface UGCVideoTimelineProps {
  segments: MediaSegment[];
  totalDurationSeconds: number;
  showTimeLabels?: boolean;
  onReorderSegments?: (fromIndex: number, toIndex: number) => void;
  onUpdateSegment?: (mediaId: string, updates: Partial<{ time: number; duration: number; fit: 'contain' | 'cover'; zoom: number; fade_in: boolean; fade_out: boolean; volume: number; trim_start: number; trim_end: number }>) => void;
  onRemoveSegment?: (mediaId: string) => void;
  segmentThumbnails?: Record<string, string>;
  segmentVideoUrls?: Record<string, string>;
  onPlaceMediaOnTimeline?: (mediaId: string, timeSec: number) => void;
  musicLane?: { title: string; durationLabel: string; waveformSrc?: string } | null;
  /** Current preview playhead position in seconds — renders a red playhead line. */
  currentPlayheadTime?: number;
  /** Called when the user clicks the ruler or empty track area to seek. */
  onSeek?: (timeSec: number) => void;
  /** Text overlays track data. */
  textOverlays?: TextOverlay[];
  onAddTextOverlay?: (timeSec: number) => void;
  onUpdateTextOverlay?: (id: string, updates: Partial<TextOverlay>) => void;
  onRemoveTextOverlay?: (id: string) => void;
  /** Called when user clicks a text-overlay pill in the timeline — parent uses this to focus the overlay properties panel. */
  onSelectTextOverlay?: (id: string) => void;
  /** Called when user clicks a segment clip in the timeline — parent uses this to open segment properties panel. */
  onSelectSegment?: (mediaId: string) => void;
  /** Controlled selection from the parent (e.g. canvas selection). When provided,
   *  overrides the timeline's internal selectedMediaId so canvas + timeline stay in sync. */
  selectedSegmentId?: string | null;
}

export const UGCVideoTimeline: React.FC<UGCVideoTimelineProps> = ({
  segments,
  totalDurationSeconds,
  showTimeLabels: _showTimeLabels = false,
  onReorderSegments: _onReorderSegments,
  onUpdateSegment,
  onRemoveSegment,
  segmentThumbnails,
  segmentVideoUrls,
  onPlaceMediaOnTimeline,
  musicLane,
  currentPlayheadTime,
  onSeek,
  textOverlays = [],
  onAddTextOverlay,
  onUpdateTextOverlay,
  onRemoveTextOverlay,
  onSelectTextOverlay,
  onSelectSegment,
  selectedSegmentId: controlledSelectedSegmentId,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const videoTrackRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const segmentsRef = useRef<MediaSegment[]>(segments);

  const [internalSelectedMediaId, setSelectedMediaId] = useState<string | null>(null);
  // Controlled prop (from parent canvas) wins when supplied so timeline + canvas stay in sync.
  const selectedMediaId = controlledSelectedSegmentId !== undefined ? controlledSelectedSegmentId : internalSelectedMediaId;
  const [selectedTextId, setSelectedTextId] = useState<string | null>(null);
  const [pxPerSec, setPxPerSec] = useState(DEFAULT_PIXELS_PER_SECOND);
  /** Keeps the time under the cursor fixed while zooming (set before pxPerSec changes). */
  const pendingZoomAnchorRef = useRef<{ anchorTime: number; cursorX: number } | null>(null);
  const pxPerSecRef = useRef(pxPerSec);
  pxPerSecRef.current = pxPerSec;

  useEffect(() => {
    segmentsRef.current = segments;
  }, [segments]);

  // Precision follows zoom: snapping and trim rounding keep tightening as the user zooms in,
  // down to ~frame level (0.01s) at max zoom — "as small as the user wants" without fighting
  // coarse snapping. Hold Alt during any drag/trim for unsnapped 0.01s precision at any zoom.
  const snapSec = pxPerSec >= 240 ? 0.01 : pxPerSec >= 120 ? 0.02 : pxPerSec >= 48 ? 0.05 : 0.25;
  const trimStep = pxPerSec >= 240 ? 0.01 : pxPerSec >= 120 ? 0.02 : pxPerSec >= 48 ? 0.05 : 0.1;

  const total = Math.max(totalDurationSeconds, 0.001);
  const timelineContentWidthPx = Math.round(total * pxPerSec);

  const zoomTimelineTo = useCallback((next: number, anchorClientX?: number) => {
    const clamped = Math.max(MIN_PIXELS_PER_SECOND, Math.min(MAX_PIXELS_PER_SECOND, next));
    const sc = scrollContainerRef.current;
    const current = pxPerSecRef.current;
    if (sc && clamped !== current) {
      const cursorX = anchorClientX !== undefined
        ? anchorClientX - sc.getBoundingClientRect().left
        : sc.clientWidth / 2;
      pendingZoomAnchorRef.current = { anchorTime: (sc.scrollLeft + cursorX) / current, cursorX };
    }
    setPxPerSec(clamped);
  }, []);

  useLayoutEffect(() => {
    const anchor = pendingZoomAnchorRef.current;
    const sc = scrollContainerRef.current;
    if (anchor && sc) {
      sc.scrollLeft = Math.max(0, anchor.anchorTime * pxPerSec - anchor.cursorX);
      pendingZoomAnchorRef.current = null;
    }
  }, [pxPerSec]);

  // Ctrl/Cmd + scroll zooms around the cursor. Native non-passive listener — React's
  // synthetic wheel handlers are passive, so preventDefault (to stop page zoom) needs this.
  useEffect(() => {
    const sc = scrollContainerRef.current;
    if (!sc) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.25 : 0.8;
      zoomTimelineTo(pxPerSecRef.current * factor, e.clientX);
    };
    sc.addEventListener('wheel', onWheel, { passive: false });
    return () => sc.removeEventListener('wheel', onWheel);
  }, [zoomTimelineTo]);
  const canMove = Boolean(onUpdateSegment && segments.length > 0);
  const canResize = Boolean(onUpdateSegment);
  const canAcceptLibraryDrop = Boolean(onPlaceMediaOnTimeline);
  const showTextTrack = Boolean(onAddTextOverlay || textOverlays.length > 0);

  const imageSegments = segments.filter((s) => s.segment_type !== 'video');
  const videoSegments = segments.filter((s) => s.segment_type === 'video');

  useEffect(() => {
    if (selectedMediaId && !segments.some((s) => s.media_id === selectedMediaId)) {
      setSelectedMediaId(null);
    }
  }, [segments, selectedMediaId]);

  useEffect(() => {
    if (selectedTextId && !textOverlays.some((t) => t.id === selectedTextId)) {
      setSelectedTextId(null);
    }
  }, [textOverlays, selectedTextId]);

  /**
   * Pointer-based segment move: the clip follows the cursor live (snapped unless Alt is held),
   * replacing the old HTML5 drag-and-drop which only showed an OS ghost image and jumped the
   * clip on drop. A 3px threshold keeps plain clicks (select/seek) working.
   */
  const handleSegmentMovePointerDown = (e: React.PointerEvent, segment: MediaSegment) => {
    if (!canMove || !onUpdateSegment) return;
    if (e.button !== 0) return;
    if (e.target instanceof HTMLElement && (e.target.closest('[data-timeline-no-drag]') || e.target.closest('[role="slider"]'))) {
      return;
    }
    const startClientX = e.clientX;
    const startTime = segment.time;
    const duration = segment.duration;
    const mediaId = segment.media_id;
    let didDrag = false;

    const onPointerMove = (moveE: PointerEvent) => {
      const deltaPx = moveE.clientX - startClientX;
      if (!didDrag && Math.abs(deltaPx) < 3) return;
      didDrag = true;
      moveE.preventDefault();
      let newTime = startTime + deltaPx / pxPerSec;
      if (!moveE.altKey) newTime = roundToStep(newTime, snapSec);
      newTime = Math.max(0, Math.min(newTime, total - duration));
      onUpdateSegment(mediaId, { time: parseFloat(newTime.toFixed(2)) });
    };
    const onPointerUp = () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
    };
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
  };

  const timeFromTrackClientX = (clientX: number, trackEl: HTMLDivElement) => {
    const rect = trackEl.getBoundingClientRect();
    const borderLeft = parseFloat(getComputedStyle(trackEl).borderLeftWidth) || 0;
    const contentX = clientX - rect.left - borderLeft;
    let time = (contentX / timelineContentWidthPx) * total;
    time = roundToStep(time, snapSec);
    return Math.max(0, Math.min(time, total - 0.001));
  };

  const handleRulerClick = (e: React.MouseEvent) => {
    if (!onSeek || !trackRef.current) return;
    const time = timeFromTrackClientX(e.clientX, trackRef.current);
    onSeek(time);
  };

  /** Library → timeline drops still use HTML5 DnD (cross-component drag); timeline-internal
   *  moves are pointer-based (see handleSegmentMovePointerDown). */
  const handleTrackDragOver = (e: React.DragEvent) => {
    const types = Array.from(e.dataTransfer.types);
    const fromLibrary =
      canAcceptLibraryDrop &&
      (types.includes(UGC_LIBRARY_MEDIA_DRAG_TYPE) || types.includes('text/plain'));
    if (!fromLibrary) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const readLibraryMediaIdFromDataTransfer = (dt: DataTransfer): string => {
    const primary = dt.getData(UGC_LIBRARY_MEDIA_DRAG_TYPE).trim();
    if (primary) return primary;
    const plain = dt.getData('text/plain').trim();
    if (plain.startsWith('__UGC_MEDIA__:')) return plain.slice('__UGC_MEDIA__:'.length).trim();
    return '';
  };

  const handleTrackDrop = (e: React.DragEvent, trackEl: HTMLDivElement | null) => {
    if (!trackEl) return;
    e.preventDefault();
    e.stopPropagation();
    const libraryMediaId = onPlaceMediaOnTimeline ? readLibraryMediaIdFromDataTransfer(e.dataTransfer) : '';
    if (libraryMediaId && onPlaceMediaOnTimeline) {
      onPlaceMediaOnTimeline(libraryMediaId, timeFromTrackClientX(e.clientX, trackEl));
    }
  };

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent, segment: MediaSegment) => {
      e.preventDefault();
      e.stopPropagation();
      if (!canResize || !onUpdateSegment || !trackRef.current) return;
      const startClientX = e.clientX;
      const startDuration = segment.duration;
      const startTime = segment.time;
      const mediaId = segment.media_id;

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (!trackRef.current) return;
        const durationDelta = (moveEvent.clientX - startClientX) * (total / timelineContentWidthPx);
        let newDuration = startDuration + durationDelta;
        const currentSegments = segmentsRef.current;
        const currentSegment = currentSegments.find((s) => s.media_id === mediaId);
        const segmentTime = currentSegment?.time ?? startTime;
        const maxDuration = maxDurationForSegment(currentSegments, mediaId, segmentTime, total);
        newDuration = Math.min(Math.max(newDuration, MIN_SEGMENT_DURATION_SEC), maxDuration);
        onUpdateSegment(mediaId, { duration: newDuration });
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [canResize, onUpdateSegment, total, timelineContentWidthPx]
  );

  const handleTextOverlayMoveMouseDown = useCallback(
    (e: React.MouseEvent, overlay: TextOverlay) => {
      e.preventDefault();
      e.stopPropagation();
      setSelectedTextId(overlay.id);
      setSelectedMediaId(null);
      onSelectTextOverlay?.(overlay.id);
      // Seek video preview to the overlay's start time so it becomes visible
      onSeek?.(overlay.time);
      if (!onUpdateTextOverlay) return;
      const startClientX = e.clientX;
      const startTime = overlay.time;
      const duration = overlay.duration;
      const overlayId = overlay.id;

      const onMouseMove = (moveEvent: MouseEvent) => {
        const timeDelta = ((moveEvent.clientX - startClientX) / timelineContentWidthPx) * total;
        let newTime = startTime + timeDelta;
        if (!moveEvent.altKey) newTime = roundToStep(newTime, snapSec);
        newTime = Math.max(0, Math.min(newTime, total - duration));
        onUpdateTextOverlay(overlayId, { time: parseFloat(newTime.toFixed(2)) });
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [onUpdateTextOverlay, onSelectTextOverlay, onSeek, timelineContentWidthPx, total, snapSec]
  );


  const textLaneMap = assignTextOverlayLanes(textOverlays);
  const numTextLanes = textOverlays.length === 0 ? 1 : Math.max(...Array.from(textLaneMap.values())) + 1;
  const textTrackHeight = Math.max(TRACK_HEIGHT, numTextLanes * TEXT_LANE_HEIGHT + 4);

  // Stack overlapping/duplicate video clips into vertical lanes (same behavior as the text
  // track) so colliding clips don't render on top of each other and stay individually grabbable.
  const videoLanes = assignLanes(videoSegments);
  const numVideoLanes = videoSegments.length === 0 ? 1 : Math.max(...videoLanes) + 1;

  // Images stack into lanes exactly like videos/text: overlapping clips get their own row
  // instead of rendering on top of each other.
  const imageLanes = assignLanes(imageSegments);
  const numImageLanes = imageSegments.length === 0 ? 1 : Math.max(...imageLanes) + 1;
  const imageTrackHeight = Math.max(TRACK_HEIGHT, numImageLanes * VIDEO_LANE_HEIGHT);
  const videoTrackHeight = Math.max(TRACK_HEIGHT, numVideoLanes * VIDEO_LANE_HEIGHT);

  // Tick spacing adapts to zoom so labels stay ~60–120px apart instead of vanishing
  // off-screen (zoomed in) or piling up (zoomed out).
  const majorTickIntervalSec =
    pxPerSec >= 240 ? 0.25 : pxPerSec >= 120 ? 0.5 : pxPerSec >= 60 ? 1 : pxPerSec >= 30 ? 2 : 5;
  const majorTicks: number[] = [];
  for (let t = 0; t <= total; t = parseFloat((t + majorTickIntervalSec).toFixed(2))) majorTicks.push(t);
  if (majorTicks[majorTicks.length - 1] < total) majorTicks.push(Math.ceil(total));
  const formatTick = (t: number) => (majorTickIntervalSec < 1 ? `${parseFloat(t.toFixed(2))}s` : `${t}s`);

  const trackGridBackground = `repeating-linear-gradient(to right, #E5E5E5 0px, #E5E5E5 1px, transparent 1px, transparent ${pxPerSec}px)`;
  const showMusicLane = Boolean(musicLane?.title);

  const playheadLeftPct =
    currentPlayheadTime !== undefined && total > 0
      ? Math.min(100, (currentPlayheadTime / total) * 100)
      : null;

  const handleEmptyTrackClick = (e: React.MouseEvent<HTMLDivElement>, trackEl: HTMLDivElement | null, onAdd?: (t: number) => void) => {
    if (e.target !== e.currentTarget) return;
    if (!trackEl) return;
    if (onAdd) {
      onAdd(timeFromTrackClientX(e.clientX, trackEl));
    } else if (onSeek) {
      onSeek(timeFromTrackClientX(e.clientX, trackEl));
    }
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex min-w-0 gap-2">
        {/* Label column */}
        <div className="flex flex-shrink-0 flex-col gap-0.5" style={{ width: LABEL_COLUMN_WIDTH }}>
          {/* Zoom controls — aligned with the ruler row. Ctrl/Cmd+scroll also zooms at the cursor. */}
          <div className="flex flex-shrink-0 items-center gap-0.5" style={{ height: 20 }} title="Timeline zoom (Ctrl+scroll on the timeline)">
            <button
              type="button"
              onClick={() => zoomTimelineTo(pxPerSec / 1.5)}
              disabled={pxPerSec <= MIN_PIXELS_PER_SECOND}
              aria-label="Zoom timeline out"
              className="flex h-4 w-4 items-center justify-center rounded text-[11px] font-bold leading-none text-[#64748b] hover:bg-gray-200 hover:text-[#1F2937] disabled:opacity-40"
            >−</button>
            <span className="w-8 text-center font-mono text-[9px] text-[#94a3b8] select-none">
              {Math.round((pxPerSec / DEFAULT_PIXELS_PER_SECOND) * 100)}%
            </span>
            <button
              type="button"
              onClick={() => zoomTimelineTo(pxPerSec * 1.5)}
              disabled={pxPerSec >= MAX_PIXELS_PER_SECOND}
              aria-label="Zoom timeline in"
              className="flex h-4 w-4 items-center justify-center rounded text-[11px] font-bold leading-none text-[#64748b] hover:bg-gray-200 hover:text-[#1F2937] disabled:opacity-40"
            >+</button>
          </div>
          <div className="flex flex-shrink-0 items-center text-[10px] font-semibold uppercase tracking-wider text-[#64748b]" style={{ height: imageTrackHeight }}>
            Images
          </div>
          <div className="flex flex-shrink-0 items-center text-[10px] font-semibold uppercase tracking-wider text-[#0369a1]" style={{ height: videoTrackHeight }}>
            Videos
          </div>
          {showTextTrack && (
            <div className="flex flex-shrink-0 items-center text-[10px] font-semibold uppercase tracking-wider text-amber-600" style={{ height: textTrackHeight }}>
              Text
            </div>
          )}
          {showMusicLane && (
            <div className="flex flex-shrink-0 items-center text-[10px] font-semibold uppercase tracking-wider text-[#64748b]" style={{ height: MUSIC_TRACK_HEIGHT }}>
              Music
            </div>
          )}
          <div className="flex flex-shrink-0 items-center text-[10px] font-semibold uppercase tracking-wider text-[#64748b]" style={{ height: TRACK_HEIGHT }}>
            Base video
          </div>
        </div>

        {/* Scrollable tracks */}
        <div ref={scrollContainerRef} className="custom-scrollbar min-w-0 flex-1 overflow-x-auto overflow-y-hidden">
          <div
            className="relative flex flex-col gap-0.5"
            style={{ minWidth: timelineContentWidthPx, width: timelineContentWidthPx }}
          >
            {/* Playhead */}
            {playheadLeftPct !== null && (
              <div
                className="pointer-events-none absolute top-0 bottom-0 z-30 flex flex-col items-center"
                style={{ left: `${playheadLeftPct}%` }}
              >
                <div className="h-2 w-2 rounded-full bg-red-500 -mt-0.5" />
                <div className="flex-1 w-0.5 bg-red-500/80" />
              </div>
            )}

            {/* Ruler */}
            <div
              className={`relative h-5 w-full flex-shrink-0 select-none ${onSeek ? 'cursor-pointer' : ''}`}
              style={{ width: timelineContentWidthPx }}
              onClick={handleRulerClick}
              title={onSeek ? 'Click to seek preview' : undefined}
            >
              {majorTicks.map((t) => (
                <span
                  key={t}
                  className="absolute -translate-x-1/2 text-[10px] font-medium text-[#94a3b8]"
                  style={{ left: `${(t / total) * 100}%` }}
                >
                  {formatTick(t)}
                </span>
              ))}
            </div>

            {/* IMAGES TRACK */}
            <div
              ref={trackRef}
              className="relative w-full flex-shrink-0 overflow-hidden rounded-lg border border-[#E5E5E5] bg-[#FAFAFA]"
              style={{ width: timelineContentWidthPx, height: imageTrackHeight, backgroundImage: trackGridBackground }}
              onDragOver={handleTrackDragOver}
              onDrop={(e) => handleTrackDrop(e, trackRef.current)}
              onClick={(e) => handleEmptyTrackClick(e, trackRef.current)}
            >
              {imageSegments.length === 0 && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-4 text-center text-[11px] text-[#94a3b8]">
                  No image clips yet. Select images above to place them here.
                </div>
              )}
              {imageSegments.map((seg, idx) => {
                const leftPct = (seg.time / total) * 100;
                const widthPct = (seg.duration / total) * 100;
                const minSegmentWidth = THUMB_BOX_WIDTH_PX + (canResize ? RESIZE_HANDLE_WIDTH_PX : 0);
                const isSelected = selectedMediaId === seg.media_id;
                const lane = imageLanes[idx] ?? 0;
                const clipTop = lane * VIDEO_LANE_HEIGHT + 4;
                const clipHeight = VIDEO_LANE_HEIGHT - 8;
                return (
                  <div
                    key={`img-${seg.media_id}-${idx}`}
                    onPointerDown={(e) => handleSegmentMovePointerDown(e, seg)}
                    onDragOver={handleTrackDragOver}
                    onClick={(e) => { e.stopPropagation(); setSelectedMediaId(seg.media_id); setSelectedTextId(null); onSelectSegment?.(seg.media_id); }}
                    className={`absolute flex overflow-hidden rounded-md border shadow-sm ${canMove ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${isSelected ? 'border-[#e4b372] ring-2 ring-[#e4b372]/50 ring-offset-1' : 'border-[#2f4941]/25 hover:border-[#e4b372]/60'}`}
                    style={{ left: `${leftPct}%`, width: `${Math.max(widthPct, 2)}%`, minWidth: minSegmentWidth, top: clipTop, height: clipHeight }}
                    title={`${seg.duration.toFixed(1)}s${seg.fade_in ? ' · fade in' : ''}${seg.fade_out ? ' · fade out' : ''}`}
                  >
                    <div
                      className={`relative flex flex-shrink-0 items-center justify-center overflow-hidden rounded-l ${segmentThumbnails?.[seg.media_id] ? 'bg-white' : 'bg-[#2f4941]'}`}
                      style={{ width: THUMB_BOX_WIDTH_PX }}
                    >
                      {segmentThumbnails?.[seg.media_id] ? (
                        <img src={segmentThumbnails[seg.media_id]} alt="" className="pointer-events-none h-full w-full object-cover" />
                      ) : (
                        <span className="truncate px-1 text-[10px] font-medium text-white/90">{idx + 1}</span>
                      )}
                      {/* Fade badges */}
                      {seg.fade_in && <div className="absolute left-0 top-0 bottom-0 w-3 bg-gradient-to-r from-black/40 to-transparent pointer-events-none" />}
                      {seg.fade_out && <div className="absolute right-0 top-0 bottom-0 w-3 bg-gradient-to-l from-black/40 to-transparent pointer-events-none" />}
                    </div>
                    <div className="min-w-0 flex-1 border-y border-r border-[#2f4941]/20 bg-[#2f4941]/55" aria-hidden />
                    {canResize && (
                      <div
                        role="slider"
                        aria-label={`Resize segment ${idx + 1}`}
                        className="w-2 flex-shrink-0 cursor-col-resize rounded-r border-r border-[#2f4941]/40 bg-[#2f4941]/85 hover:bg-[#1e3128]"
                        style={{ minWidth: RESIZE_HANDLE_WIDTH_PX }}
                        onMouseDown={(e) => handleResizeMouseDown(e, seg)}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* VIDEOS TRACK */}
            <div
              ref={videoTrackRef}
              className="relative w-full flex-shrink-0 overflow-hidden rounded-lg border border-brand-primary/30 bg-brand-primary/10"
              style={{ width: timelineContentWidthPx, height: videoTrackHeight, backgroundImage: trackGridBackground }}
              onClick={(e) => handleEmptyTrackClick(e, videoTrackRef.current)}
            >
              {videoSegments.length === 0 && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-4 text-center text-[11px] text-theme-fg-muted">
                  No video clips yet. Search Pexels Videos above to add stock video scenes here.
                </div>
              )}
              {videoSegments.map((seg, idx) => {
                const leftPct = (seg.time / total) * 100;
                const widthPct = (seg.duration / total) * 100;
                const minSegmentWidth = 60; // enough for handles + a few frames
                const isSelected = selectedMediaId === seg.media_id;
                const trimStart = seg.trim_start ?? 0;
                const trimEnd = seg.trim_end ?? 0;
                const hasTrim = trimStart > 0 || trimEnd > 0;
                const lane = videoLanes[idx] ?? 0;
                const clipTop = lane * VIDEO_LANE_HEIGHT + 4;
                const clipHeight = VIDEO_LANE_HEIGHT - 8;
                return (
                  <div
                    key={`vid-${seg.media_id}-${idx}`}
                    onPointerDown={(e) => handleSegmentMovePointerDown(e, seg)}
                    onClick={(e) => { e.stopPropagation(); setSelectedMediaId(seg.media_id); setSelectedTextId(null); onSelectSegment?.(seg.media_id); onSeek?.(seg.time); }}
                    className={`absolute flex overflow-visible rounded-md border shadow-sm ${canMove ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${isSelected ? 'border-brand-primary ring-2 ring-brand-primary/50 ring-offset-1' : 'border-brand-primary/30 hover:border-brand-primary/60'}`}
                    style={{ left: `${leftPct}%`, width: `${Math.max(widthPct, 2)}%`, minWidth: minSegmentWidth, top: clipTop, height: clipHeight }}
                    title={`Video · ${seg.duration.toFixed(1)}s${seg.volume ? ` · vol ${seg.volume}%` : ' · muted'}${seg.fade_in ? ' · fade in' : ''}${seg.fade_out ? ' · fade out' : ''}${hasTrim ? ` · trimmed` : ''}`}
                  >
                    {/* Left trim handle — drag right to cut from the start */}
                    {onUpdateSegment && (
                      <div
                        data-timeline-no-drag
                        className="absolute top-0 bottom-0 left-0 w-3 cursor-ew-resize bg-orange-400 hover:bg-orange-300 rounded-l flex items-center justify-center gap-px"
                        style={{ zIndex: 30 }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const startX = e.clientX;
                          const startTime = seg.time;
                          const startDuration = seg.duration;
                          const startTrim = trimStart;
                          const trackEl = videoTrackRef.current;
                          if (!trackEl) return;
                          const rect = trackEl.getBoundingClientRect();
                          const pxPerSec = rect.width / total;
                          const handleMouseMove = (moveE: MouseEvent) => {
                            const deltaX = moveE.clientX - startX;
                            const deltaSec = deltaX / pxPerSec;
                            // Drag right (positive) trims from start; drag left un-trims (only if trim_start > 0)
                            const minDelta = -startTrim;
                            const maxDelta = startDuration - MIN_SEGMENT_DURATION_SEC;
                            const clampedDelta = Math.max(minDelta, Math.min(maxDelta, deltaSec));
                            const newTime = Math.max(0, startTime + clampedDelta);
                            const newDuration = startDuration - clampedDelta;
                            const newTrimStart = startTrim + clampedDelta;
                            const step = moveE.altKey ? 0.01 : trimStep;
                            onUpdateSegment(seg.media_id, {
                              time: roundToStep(newTime, step),
                              duration: roundToStep(newDuration, step),
                              trim_start: roundToStep(newTrimStart, step),
                            });
                          };
                          const handleMouseUp = () => {
                            document.removeEventListener('mousemove', handleMouseMove);
                            document.removeEventListener('mouseup', handleMouseUp);
                          };
                          document.addEventListener('mousemove', handleMouseMove);
                          document.addEventListener('mouseup', handleMouseUp);
                        }}
                        title="Drag right to trim start · drag left to extend"
                      >
                        <div className="h-4 w-0.5 bg-white rounded" />
                        <div className="h-4 w-0.5 bg-white rounded" />
                      </div>
                    )}
                    {/* Filmstrip — shows actual video frames distributed across segment width */}
                    <div className="relative flex flex-1 min-w-0 overflow-hidden rounded bg-brand-primary-dark">
                      {(() => {
                        const videoUrl = segmentVideoUrls?.[seg.media_id];
                        const thumbUrl = segmentThumbnails?.[seg.media_id];
                        const NUM_FRAMES = 4;
                        return Array.from({ length: NUM_FRAMES }).map((_, i) => {
                          const progress = i / (NUM_FRAMES - 1);
                          const frameTime = trimStart + (seg.duration * progress);
                          return (
                            <div
                              key={i}
                              className="relative flex-1 min-w-0 overflow-hidden border-r border-brand-primary-dark/40 last:border-r-0"
                            >
                              {videoUrl ? (
                                <video
                                  src={videoUrl}
                                  muted
                                  preload="metadata"
                                  playsInline
                                  className="pointer-events-none h-full w-full object-cover"
                                  ref={(el) => {
                                    if (!el) return;
                                    const setFrame = () => {
                                      if (Math.abs(el.currentTime - frameTime) > 0.05) {
                                        try { el.currentTime = frameTime; } catch {}
                                      }
                                      if (!el.paused) el.pause();
                                    };
                                    if (el.readyState >= 1) setFrame();
                                    else el.addEventListener('loadedmetadata', setFrame, { once: true });
                                  }}
                                />
                              ) : thumbUrl ? (
                                <img src={thumbUrl} alt="" className="pointer-events-none h-full w-full object-cover opacity-80" />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center">
                                  <svg className="h-4 w-4 text-white/80" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                                    <path d="M4 6a2 2 0 012-2h8a2 2 0 012 2v4l4-2v8l-4-2v4a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
                                  </svg>
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()}
                      {seg.volume !== undefined && seg.volume > 0 && (
                        <div className="absolute bottom-0.5 right-1 rounded bg-brand-primary-dark/80 px-1 text-[8px] font-bold text-white leading-none py-0.5 pointer-events-none">
                          {seg.volume}%
                        </div>
                      )}
                    </div>
                    {/* Right trim handle — drag left to cut from end, drag right to extend */}
                    {onUpdateSegment && (
                      <div
                        data-timeline-no-drag
                        className="absolute top-0 bottom-0 right-0 w-3 cursor-ew-resize bg-orange-400 hover:bg-orange-300 rounded-r flex items-center justify-center gap-px"
                        style={{ zIndex: 30 }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const startX = e.clientX;
                          const startDuration = seg.duration;
                          const startTrimEnd = trimEnd;
                          const trackEl = videoTrackRef.current;
                          if (!trackEl) return;
                          const rect = trackEl.getBoundingClientRect();
                          const pxPerSec = rect.width / total;
                          const handleMouseMove = (moveE: MouseEvent) => {
                            const deltaX = moveE.clientX - startX;
                            const deltaSec = deltaX / pxPerSec;
                            // Drag left trims from end; drag right extends clip or un-trims
                            const minDelta = -(startDuration - MIN_SEGMENT_DURATION_SEC);
                            const maxDelta = Math.max(0, total - seg.time - startDuration);
                            const clampedDelta = Math.max(minDelta, Math.min(maxDelta, deltaSec));
                            const newDuration = startDuration + clampedDelta;
                            const newTrimEnd = Math.max(0, startTrimEnd - clampedDelta);
                            const step = moveE.altKey ? 0.01 : trimStep;
                            onUpdateSegment(seg.media_id, {
                              duration: roundToStep(newDuration, step),
                              trim_end: roundToStep(newTrimEnd, step),
                            });
                          };
                          const handleMouseUp = () => {
                            document.removeEventListener('mousemove', handleMouseMove);
                            document.removeEventListener('mouseup', handleMouseUp);
                          };
                          document.addEventListener('mousemove', handleMouseMove);
                          document.addEventListener('mouseup', handleMouseUp);
                        }}
                        title="Drag left to trim end · drag right to extend"
                      >
                        <div className="h-4 w-0.5 bg-white rounded" />
                        <div className="h-4 w-0.5 bg-white rounded" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* TEXT OVERLAYS TRACK */}
            {showTextTrack && (
              <div
                className="relative w-full flex-shrink-0 overflow-visible rounded-lg border border-amber-200 bg-amber-50/50"
                style={{ width: timelineContentWidthPx, height: textTrackHeight, backgroundImage: trackGridBackground }}
                onClick={(e) => {
                  if (e.target !== e.currentTarget) return;
                  if (onSeek && trackRef.current) onSeek(timeFromTrackClientX(e.clientX, trackRef.current));
                }}
              >
                {textOverlays.length === 0 && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-4 text-center text-[11px] text-amber-400">
                    Use the Text button above to add an overlay
                  </div>
                )}
                {textOverlays.map((overlay) => {
                  const leftPct = (overlay.time / total) * 100;
                  const widthPct = (overlay.duration / total) * 100;
                  const isSelected = selectedTextId === overlay.id;
                  const lane = textLaneMap.get(overlay.id) ?? 0;
                  const pillTop = lane * TEXT_LANE_HEIGHT + 2;
                  const pillHeight = TEXT_LANE_HEIGHT - 4;
                  const styleColors: Record<string, string> = {
                    title: 'bg-amber-500/90 border-amber-600',
                    subtitle: 'bg-amber-400/80 border-amber-500',
                    cta: 'bg-orange-500/90 border-orange-600',
                    'lower-third': 'bg-yellow-500/80 border-yellow-600',
                  };
                  const colorClass = styleColors[overlay.style ?? 'subtitle'] ?? styleColors.subtitle;
                  return (
                    <div
                      key={overlay.id}
                      className={`absolute flex cursor-grab active:cursor-grabbing items-center overflow-visible rounded-md border shadow-sm ${colorClass} ${isSelected ? 'ring-2 ring-amber-500/60 ring-offset-1' : ''}`}
                      style={{ left: `${leftPct}%`, width: `${Math.max(widthPct, 1.5)}%`, minWidth: 48, top: pillTop, height: pillHeight }}
                      onMouseDown={(e) => handleTextOverlayMoveMouseDown(e, overlay)}
                      title={`${overlay.duration.toFixed(1)}s — drag to move`}
                    >
                      {/* Left trim handle */}
                      {onUpdateTextOverlay && (
                        <div
                          className="absolute top-0 bottom-0 left-0 w-3 cursor-ew-resize bg-amber-800 hover:bg-amber-900 rounded-l flex items-center justify-center"
                          style={{ zIndex: 10 }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const startX = e.clientX;
                            const startTime = overlay.time;
                            const startDuration = overlay.duration;
                            const onMouseMove = (moveE: MouseEvent) => {
                              const delta = ((moveE.clientX - startX) / timelineContentWidthPx) * total;
                              const minDelta = -startTime;
                              const maxDelta = startDuration - MIN_SEGMENT_DURATION_SEC;
                              const clamped = Math.max(minDelta, Math.min(maxDelta, delta));
                              const step = moveE.altKey ? 0.01 : trimStep;
                              onUpdateTextOverlay(overlay.id, {
                                time: roundToStep(startTime + clamped, step),
                                duration: roundToStep(startDuration - clamped, step),
                              });
                            };
                            const onMouseUp = () => {
                              document.removeEventListener('mousemove', onMouseMove);
                              document.removeEventListener('mouseup', onMouseUp);
                            };
                            document.addEventListener('mousemove', onMouseMove);
                            document.addEventListener('mouseup', onMouseUp);
                          }}
                        >
                          <div className="h-4 w-0.5 bg-white/80 rounded" />
                        </div>
                      )}
                      <span className="min-w-0 flex-1 truncate px-4 text-[10px] font-semibold text-white leading-tight select-none">
                        {overlay.text || 'New text'}
                      </span>
                      {/* Right trim handle */}
                      {onUpdateTextOverlay && (
                        <div
                          className="absolute top-0 bottom-0 right-0 w-3 cursor-ew-resize bg-amber-800 hover:bg-amber-900 rounded-r flex items-center justify-center"
                          style={{ zIndex: 10 }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const startX = e.clientX;
                            const startDuration = overlay.duration;
                            const startTime = overlay.time;
                            const onMouseMove = (moveE: MouseEvent) => {
                              const delta = ((moveE.clientX - startX) / timelineContentWidthPx) * total;
                              const newDuration = Math.max(MIN_SEGMENT_DURATION_SEC, Math.min(total - startTime, startDuration + delta));
                              const step = moveE.altKey ? 0.01 : trimStep;
                              onUpdateTextOverlay(overlay.id, { duration: roundToStep(newDuration, step) });
                            };
                            const onMouseUp = () => {
                              document.removeEventListener('mousemove', onMouseMove);
                              document.removeEventListener('mouseup', onMouseUp);
                            };
                            document.addEventListener('mousemove', onMouseMove);
                            document.addEventListener('mouseup', onMouseUp);
                          }}
                        >
                          <div className="h-4 w-0.5 bg-white/80 rounded" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* MUSIC TRACK */}
            {showMusicLane && musicLane && (
              <div
                className="relative w-full flex-shrink-0 overflow-hidden rounded-lg border border-emerald-700/40 bg-emerald-50"
                style={{ width: timelineContentWidthPx, height: MUSIC_TRACK_HEIGHT, backgroundImage: trackGridBackground }}
              >
                <div className="absolute inset-y-1.5 left-1 right-1 flex items-center gap-2 rounded-md border border-emerald-600/30 bg-emerald-600/90 px-2 shadow-sm" title={musicLane.title}>
                  {musicLane.waveformSrc ? (
                    <UGCAudioWaveform src={musicLane.waveformSrc} barCount={44} className="h-7 min-w-0 flex-1 text-emerald-100" barClassName="bg-emerald-100" />
                  ) : (
                    <UGCMusicWaveform className="h-7 min-w-0 flex-1 text-emerald-100" barClassName="bg-emerald-100" bars={40} />
                  )}
                  <div className="flex flex-shrink-0 flex-col items-end justify-center pr-1 text-right text-emerald-50">
                    <span className="max-w-[140px] truncate text-[10px] font-semibold leading-tight sm:max-w-[200px]">{musicLane.title}</span>
                    <span className="font-mono text-[9px] opacity-90">{musicLane.durationLabel}</span>
                  </div>
                </div>
              </div>
            )}

            {/* BASE VIDEO TRACK */}
            <div
              className="relative w-full flex-shrink-0 overflow-hidden rounded-lg border border-[#1e3a2f] bg-[#0f1f18]"
              style={{ width: timelineContentWidthPx, height: TRACK_HEIGHT, backgroundImage: trackGridBackground }}
            >
              <div className="absolute inset-y-2 left-1 right-1 rounded-md bg-[#2f4941] shadow-inner" aria-hidden />
              <span className="pointer-events-none absolute bottom-1.5 left-2 text-[10px] font-medium text-[#a8c4b8]">Base video (full length)</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default UGCVideoTimeline;
